import React, { useEffect } from 'react'
import { css } from '@emotion/react'
import config from 'config'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import { megaphone as Megaphone } from 'assets/icons/groove/v2'

import { styles } from '../IconItem/styles'

const iconOverride = css`
  display: flex;
  align-items: center;
  justify-content: center;
  &&& svg {
    width: 18px;
    height: 18px;
  }
`

const CANNY_SDK_ID = 'canny-jssdk'
const CANNY_SDK_SRC = 'https://sdk.canny.io/sdk.js'

function ensureCannyStub() {
  if (typeof window.Canny !== 'function') {
    const canny = (...args) => {
      canny.q.push(args)
    }
    canny.q = []
    window.Canny = canny
  }
}

function loadCannySdk() {
  const existing = document.getElementById(CANNY_SDK_ID)
  if (existing && existing.dataset.cannyStatus === 'loaded') {
    return Promise.resolve()
  }
  if (window.cannySdkPromise) return window.cannySdkPromise

  ensureCannyStub()

  const script =
    existing && existing.dataset.cannyStatus === 'loading'
      ? existing
      : document.createElement('script')

  if (!existing || existing.dataset.cannyStatus !== 'loading') {
    script.id = CANNY_SDK_ID
    script.type = 'text/javascript'
    script.async = true
    script.src = CANNY_SDK_SRC
    script.dataset.cannyStatus = 'loading'
  }

  window.cannySdkPromise = new Promise((resolve, reject) => {
    script.addEventListener(
      'load',
      () => {
        script.dataset.cannyStatus = 'loaded'
        window.cannySdkPromise = undefined
        resolve()
      },
      { once: true }
    )
    script.addEventListener(
      'error',
      () => {
        script.dataset.cannyStatus = 'error'
        window.cannySdkPromise = undefined
        script.remove()
        reject(new Error('Failed to load the Canny SDK.'))
      },
      { once: true }
    )

    if (!existing || existing.dataset.cannyStatus !== 'loading') {
      const firstScript = document.getElementsByTagName('script')[0]
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript)
      } else {
        document.head.appendChild(script)
      }
    }
  })

  return window.cannySdkPromise
}

const Changelog = ({ className }) => {
  const appId = config.cannyAppId

  useEffect(
    () => {
      if (!appId) return undefined
      let isMounted = true

      const init = async () => {
        if (window.cannyChangelogInitialized) return
        try {
          await loadCannySdk()
        } catch (err) {
          return
        }
        if (!isMounted || window.cannyChangelogInitialized) return

        window.Canny('initChangelog', {
          appID: appId,
          position: 'bottom',
          align: 'right',
          theme: 'auto',
        })
        window.cannyChangelogInitialized = true
      }

      init()

      return () => {
        isMounted = false
      }
    },
    [appId]
  )

  if (!appId) return null

  return (
    <Tooltip title="What's new" strategy="fixed">
      <div
        css={[styles.base, iconOverride]}
        className={className}
        data-canny-changelog=""
      >
        <Megaphone />
      </div>
    </Tooltip>
  )
}

export default Changelog
